// src/pages/ReportIssuePage.jsx
import React, { useMemo, useState, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { FileUp, X } from "lucide-react";

export default function ReportIssuePage() {
  const { user, signInWithGoogle } = useAuth();
  const isAuthed = !!(user && user.email);

  const [category, setCategory] = useState("화면인식오류");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [files, setFiles] = useState([]); // File[]
  const [previews, setPreviews] = useState([]); // { url, name, size }[]
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const MAX_FILES = 10;
  const MAX_SIZE = 8 * 1024 * 1024; // Discord 무료 첨부 한도(파일 당 8MB)

  const CATEGORIES = useMemo(
    () => ["화면인식오류", "사이트오류", "기타오류", "문의"],
    []
  );

  // ---------- 스타일 공통 ----------
  const selectCls =
    "appearance-none inline-block rounded-lg border border-zinc-950/10 cursor-pointer px-4 py-2 pr-8 bg-white " +
    "text-sm font-semibold text-zinc-950 focus:outline-none focus:ring-1 focus:ring-indigo-500 " +
    "bg-no-repeat bg-[length:16px_16px] bg-[right_8px_center] dark:bg-zinc-900 dark:text-zinc-100 dark:border-white/10";

  const chevronSvg = encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#9CA3AF"><path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.17l3.71-2.94a.75.75 0 1 1 .92 1.18l-4.17 3.31a.75.75 0 0 1-.92 0L5.21 8.41a.75.75 0 0 1 .02-1.2z"/></svg>'
  );

  // previews 정리
  React.useEffect(() => {
    return () => previews.forEach(p => URL.revokeObjectURL(p.url));
  }, [previews]);

  // ---------- 파일 처리 ----------
  const fileInputRef = useRef(null);

  const toPreview = (file) => ({
    url: URL.createObjectURL(file),
    name: file.name,
    size: file.size,
  });

  const addFiles = useCallback(
    (fileList) => {
      const incoming = Array.from(fileList || []);
      if (!incoming.length) return;

      let newFiles = [...files];
      let newPreviews = [...previews];
      let err = "";

      for (const f of incoming) {
        if (!f.type.startsWith("image/")) {
          err = "이미지 파일만 업로드할 수 있습니다.";
          continue;
        }
        if (f.size > MAX_SIZE) {
          err = "이미지 파일은 8MB 이하만 첨부할 수 있어요.";
          continue;
        }
        if (newFiles.length >= MAX_FILES) {
          err = `이미지는 최대 ${MAX_FILES}장까지 첨부할 수 있어요.`;
          break;
        }
        newFiles.push(f);
        newPreviews.push(toPreview(f));
      }

      setFiles(newFiles);
      setPreviews(newPreviews);
      if (err) setError(err);
    },
    [files, previews, MAX_SIZE]
  );

  const onPickFiles = (e) => {
    setError("");
    addFiles(e.target.files);
    e.target.value = ""; // 같은 파일 재선택 가능하도록 초기화
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    setError("");
    addFiles(e.dataTransfer.files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const removeFileAt = (idx) => {
    const nextFiles = [...files];
    const nextPreviews = [...previews];
    URL.revokeObjectURL(nextPreviews[idx]?.url);
    nextFiles.splice(idx, 1);
    nextPreviews.splice(idx, 1);
    setFiles(nextFiles);
    setPreviews(nextPreviews);
  };

  const clearAllFiles = () => {
    previews.forEach((p) => URL.revokeObjectURL(p.url));
    setFiles([]);
    setPreviews([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ---------- 전송 ----------
  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (submitting) return;

    if (!isAuthed) {
      setError("Google 로그인한 사용자만 제보할 수 있습니다.");
      return;
    }
    if (!category || !title.trim() || !content.trim()) {
      setError("카테고리, 제목, 내용을 모두 입력해 주세요.");
      return;
    }

    if (!window.confirm("오류를 제보하시겠습니까?")) return;

    try {
      setSubmitting(true);

      const formData = new FormData();
      formData.append("category", category);
      formData.append("title", title.trim());
      formData.append("content", content.trim());
      formData.append("fromDisplayName", user.displayName || "알 수 없음");
      formData.append("fromEmail", user.email || "알 수 없음");
      formData.append("uid", user.uid || "");
      formData.append("at", new Date().toISOString());
      for (const f of files) formData.append("files", f, f.name);

      const res = await fetch("/api/report", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error("전송 실패");

      // ✅ 성공 처리
      alert("전송되었습니다. 빠르게 확인 후 이메일로 답변드리겠습니다.");
      window.location.replace(window.location.pathname);
      return;
    } catch (err) {
      setError(err.message || "전송 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-6xl sm:px-4 lg:px-0">
      {/* 헤더 */}
      <header className="mb-6">
        <h1 className="text-lg sm:text-xl font-semibold text-zinc-950 dark:text-white mb-3">
          오류 제보
        </h1>
        <p className="text-sm text-zinc-600 dark:text-zinc-400">
          제보하신 문의에 대한 답장은{" "}
          <b className="text-zinc-900 dark:text-white">Google 이메일</b>로 드립니다.
        </p>
      </header>

      {/* 카드 */}
      <section className="rounded-lg border border-zinc-950/10 bg-white dark:border-white/10 dark:bg-zinc-900 shadow-sm">
        <div className="border-b border-zinc-950/10 px-3 py-3 sm:p-4 dark:border-white/10">
          <h2 className="text-sm sm:text-base font-semibold text-zinc-950 dark:text-white flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-indigo-50 text-indigo-700 text-sm font-semibold border border-indigo-200 dark:bg-indigo-400/20 dark:text-indigo-300 dark:border-indigo-300/20">
              !
            </span>
            오류 제보 폼
          </h2>
        </div>

        <div className="px-3 py-4 sm:p-4">
          {/* 로그인 가드 */}
          {!isAuthed && (
            <Callout intent="rose">
              Google 로그인 후 제보하실 수 있습니다.&nbsp;
              <button
                type="button"
                className="btn btn-sm btn-primary ml-1"
                onClick={signInWithGoogle}
              >
                Google 로그인
              </button>
            </Callout>
          )}

          {/* 에러 메시지 */}
          {error && <Callout intent="rose">❌ {error}</Callout>}

          {/* 폼 */}
          <form onSubmit={handleSubmit} className="mt-3 space-y-4">
            {/* 작성자 */}
            <div className="text-xs text-zinc-500 dark:text-zinc-400">
              보낸이:{" "}
              <b className="text-zinc-700 dark:text-zinc-200">
                {isAuthed
                  ? `${user.displayName || "이름 없음"} · ${user.email || "이메일 없음"}`
                  : "Google 로그인 필요"}
              </b>
            </div>

            {/* 카테고리 */}
            <div>
              <label className="block text-sm font-medium mb-1">카테고리</label>
              <select
                className={selectCls + " w-full"}
                style={{ backgroundImage: `url("data:image/svg+xml,${chevronSvg}")` }}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={!isAuthed || submitting}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {/* 제목 */}
            <div>
              <label className="block text-sm font-medium mb-1">제목</label>
              <input
                type="text"
                placeholder="제목을 입력해 주세요"
                className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm dark:bg-zinc-900 dark:border-white/10 dark:text-zinc-200"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={!isAuthed || submitting}
                maxLength={120}
              />
            </div>

            {/* 내용 */}
            <div>
              <label className="block text-sm font-medium mb-1">내용</label>
              <textarea
                placeholder="오류 상황, 재현 방법, 스크린샷/영상 링크 등을 적어주세요."
                className="min-h-[160px] w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm dark:bg-zinc-900 dark:border-white/10 dark:text-zinc-200"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                disabled={!isAuthed || submitting}
              />
            </div>

            {/* 이미지 첨부 */}
            <div>
              <label className="block text-sm font-medium mb-1">이미지 첨부 (선택)</label>
              <div
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                className={[
                  "relative flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
                  dragOver
                    ? "border-indigo-400 bg-indigo-50/50 dark:bg-indigo-400/10"
                    : "border-zinc-300 dark:border-zinc-600 hover:border-indigo-400 hover:bg-indigo-50/30 dark:hover:border-indigo-400",
                ].join(" ")}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  onChange={onPickFiles}
                  disabled={!isAuthed || submitting}
                />
                <FileUp size={48} strokeWidth={1} className="mb-2" />
                <p className="text-sm text-zinc-600 dark:text-zinc-300">
                  클릭 또는 드래그하여 업로드
                </p>
                <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
                  PNG, JPG, GIF · 파일당 최대 8MB · 최대 {MAX_FILES}장
                </p>
              </div>

              {previews.length > 0 && (
                <>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-zinc-500 dark:text-zinc-400">
                      총 {files.length}개 첨부
                    </span>
                    <button
                      type="button"
                      onClick={clearAllFiles}
                      className="btn btn-sm"
                      disabled={submitting}
                    >
                      전체 제거
                    </button>
                  </div>
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {previews.map((p, idx) => (
                      <div
                        key={p.url}
                        className="relative rounded-md overflow-hidden border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-800"
                      >
                        <img
                          src={p.url}
                          alt={p.name}
                          className="w-full h-32 object-cover"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFileAt(idx);
                          }}
                          className="absolute top-1 right-1 inline-flex items-center justify-center rounded-md bg-black/50 hover:bg-black/65 text-white p-1"
                          title="제거"
                        >
                          <X size={14} />
                        </button>
                        <div className="px-2 py-1">
                          <p className="text-[11px] text-zinc-700 dark:text-zinc-200 truncate">
                            {p.name}
                          </p>
                          <p className="text-[10px] text-zinc-400 dark:text-zinc-400">
                            {(p.size / 1024).toFixed(0)} KB
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* 버튼 */}
            <div className="flex items-center gap-2 pt-2 justify-end">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!isAuthed || submitting}
              >
                {submitting ? "전송 중…" : "전송"}
              </button>
            </div>

            <p className="text-right text-xs text-zinc-500 dark:text-zinc-400 pt-1">
              답변은 로그인한 Google 이메일로 발송됩니다.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}

function Callout({ children, intent = "zinc" }) {
  const tone =
    intent === "rose"
      ? "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-400/20 dark:text-rose-300 dark:border-rose-300/20"
      : "bg-zinc-50 text-zinc-700 border-zinc-200 dark:bg-zinc-400/20 dark:text-zinc-300 dark:border-zinc-300/20";
  return (
    <div className={`mt-2 rounded-md border px-3 py-2 sm:px-3.5 sm:py-2.5 text-[12px] sm:text-sm ${tone}`}>
      {children}
    </div>
  );
}
